import CollapsibleCard from "@/components/collapsible-card"
import Button from "@/components/button"
import { Icon } from "@/icons/icon"
import {
  FormSchema,
  MachineFormSchema,
  MachineFormSchemaBuilder,
  MarketType,
  PackagingParamWidgetSchema,
  ProductFormData,
  ProductSchema,
  ProductSectionFormData,
  ProductSectionSchema,
  usePackagingForm,
  usePackagingFormContext,
} from "@/packaging/lib/form"
import useStore from "@/store"
import { useEffect, useState } from "react"
import { isNaN, padStart, toPath } from "lodash"
import RoundedButton from "@/components/rounded-button"
import { cn, EnumsOf, useDebounceByHash } from "@/lib/utils"
import {
  DepartmentSchema,
  PackagingParamSchemaType,
} from "@/packaging/lib/schema"
import {
  DropdownParamSchema,
  ParamSchema,
  ParamSchemaMode,
  ParamSchemaType,
  RadioParamSchema,
} from "@/lib/form/schema"
import RadioGroupWidget from "@/lib/form/widgets/radio-group"
import DropdownWidget from "@/lib/form/widgets/dropdown"
import LimitWidget from "@/lib/form/widgets/limit"
import { FormContextProvider } from "@/lib/form/form-context"
import ChoiceWidget from "@/lib/form/widgets/choice"
import DatePickerWidget from "@/lib/form/widgets/date-picker"
import { useFieldChange, useFieldStatus, useWatch } from "@/lib/form/form"
import { toast } from "sonner"
import { posthog } from "@/lib/posthog"
import { FormParamStatus } from "@/lib/form/types"
import LeakageWidget from "@/lib/form/widgets/leakage"
import { format } from "date-fns"
import ParamRemarksSheet from "@/ipqc/components/param-remarks-sheet"
import RemarkDetailsSheet from "@/ipqc/components/remark-details-sheet"
import { handleNetworkError } from "@/services/base"
import { useNavigate } from "react-router"
import { HTTPError } from "ky"
import { deleteProductSheet } from "@/services/packaging"
import { PackagingSheets } from "@/store/packaging"
import ConfirmActionSheet from "@/components/confirm-action-sheet"

export interface PackagingFormProps {
  canEdit?: boolean
}

export default function PackagingForm({ canEdit }: PackagingFormProps) {
  const navigate = useNavigate()
  const {
    formSchemas: forms,
    currMachineId,
    currDeptId,
    getDeptSchema,
    setMachineFormSchema,
    ...packagingStore
  } = useStore.use.packaging()
  const schema = useCurrFormSchema()
  const sessionStore = useStore.use.session()
  const { machine, department, builder } = useCurrMachine()
  const form = usePackagingForm({
    schema: machine,
    deptId: currDeptId,
  })
  const debounce = useDebounceByHash(500)
  const canAddProduct = useCanAddProduct()
  const [deleteProduct, setDeleteProduct] = useState<ProductSchema | null>(null)
  const [deleteProductIdx, setDeleteProductIdx] = useState<number>(-1)

  useInit()

  function syncFormToStore() {
    const values = form.form.getValues()
    sessionStore.setProcessUpdatedAt(
      packagingStore.shift.processId || "",
      new Date().toISOString()
    )
    packagingStore.setMachineFormData(currDeptId, values)

  }

  function useInit() {
    useEffect(() => {
      form.form.addEventHandler("change", () => {
        debounce("syncFormToStore", syncFormToStore)
      })

      return () => {
        form.form.removeEventHandler("change")
      }
    }, [form, currDeptId])
  }

  function useCurrFormSchema() {
    const [formSchema, setFormSchema] = useState<FormSchema | null>(null)

    useEffect(() => {
      const dept = forms.find((form) => form.id === currDeptId)

      if (!dept) {
        setFormSchema(null)

        return
      }

      setFormSchema(dept)
    }, [currDeptId])

    return formSchema
  }

  function useCurrMachine() {
    const [machine, setMachine] = useState<MachineFormSchema | null>(null)
    const [builder, setBuilder] = useState<MachineFormSchemaBuilder | null>(
      null
    )
    const [department, setDepartment] = useState<DepartmentSchema | null>(null)

    useEffect(() => {
      if (!schema) {
        setMachine(null)
        setBuilder(null)
        setDepartment(null)

        return
      }

      const curr = schema.machines.find((item) => item.id === currMachineId)
      const dept = getDeptSchema(schema.id)

      if (!dept) {
        setMachine(null)
        setBuilder(null)
        setDepartment(null)

        return
      }

      const currMachineSchema = dept?.machines.find(
        (machine) => machine.id === currMachineId
      )

      if (!curr || !currMachineSchema) {
        setMachine(null)
        setBuilder(null)
        setDepartment(null)

        return
      }

      setDepartment(dept)
      setMachine(curr)

      const newBuilder = new MachineFormSchemaBuilder(currMachineSchema, curr)
      setBuilder(newBuilder)
      setMachineFormSchema(schema.id, newBuilder.schema)
      form.rebuildForm({
        schema: newBuilder.schema,
        deptId: currDeptId,
        defaultValues: packagingStore.getFormData(currDeptId, curr.id),
      })
      void form.form.runAllValidators()
    }, [schema, currMachineId])

    return {
      machine,
      builder,
      department,
    }
  }

  function useCanAddProduct() {
    const [isAllowed, setIsAllowed] = useState(false)

    useEffect(() => {
      if (!canEdit) {
        setIsAllowed(false)

        return
      }

      const data = packagingStore.getFormData(currDeptId, currMachineId)
      const machineSchema = schema?.machines?.find(
        (machine) => machine.id === currMachineId
      )

      if (!data || !machineSchema) {
        setIsAllowed(false)

        return
      }

      if (machineSchema?.products?.length !== data?.products?.length) {
        setIsAllowed(false)

        return
      }

      const canAdd = data?.products?.reduce((acc, curr) => {
        if (!acc) {
          return false
        }

        const sectionsCompleted = curr?.sections?.reduce((acc, curr) => {
          if (!acc) {
            return false
          }

          return curr.status === FormParamStatus.SUBMITTED
        }, true)

        return sectionsCompleted
      }, true)

      setIsAllowed(canAdd)
    }, [packagingStore.forms, currDeptId, currMachineId, schema, canEdit])

    return isAllowed
  }

  function handleAddProduct() {
    if (builder) {
      const product = builder.addProduct()
      const certification = builder.addSection(
        product.id,
        MarketType.Domestic,
        PackagingParamSchemaType.CERTIFICATION,
        false
      )
      setMachineFormSchema(currDeptId, builder.schema)
      const index = builder.schema.products.length - 1

      if (product && certification) {
        form.setProduct(index, product)
      } else {
        const err = new Error("Error creating product")
        toast.error(err.message)
        posthog.captureException(err, { data: builder.schema })
      }
    }
  }

  if (!currDeptId || !schema) {
    return false
  }

  if (!machine?.products.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 mt-10">
        <div className="text-label-text">Please add a product</div>

        {canAddProduct && (
          <RoundedButton icon={<Icon name="Plus" />} onClick={handleAddProduct}>
            Product
          </RoundedButton>
        )}
      </div>
    )
  }

  async function handleDeleteProduct() {
    try {
      if (!builder || !deleteProduct || deleteProductIdx < 0) {
        return
      }

      const formData = form.form.getValues(`products[${deleteProductIdx}]`) as
        | ProductFormData
        | undefined
      if (!formData) {
        return
      }

      const generalSection = formData.sections.at(0)
      if (!generalSection) {
        return
      }

      const sectionStatus = generalSection.status
      const market = generalSection.parameters.at(0)?.value
      const selectedProductId = generalSection.parameters.at(1)?.value
      const skuId = generalSection.parameters.at(2)?.value

      if (
        market &&
        selectedProductId &&
        skuId &&
        sectionStatus === FormParamStatus.SUBMITTED
      ) {
        await deleteProductSheet({
          departmentId: currDeptId,
          machineId: currMachineId,
          productId: selectedProductId,
          skuId: skuId,
          market: market,
        })
      }

      builder.deleteProduct(deleteProduct.id)
      setMachineFormSchema(currDeptId, builder.schema)

      form.form.remove(`products[${deleteProductIdx}]`)
      form.rebuildForm({
        schema: builder.schema,
        deptId: currDeptId,
        defaultValues: form.form.getValues(),
      })
      await form.form.runAllValidators()
    } catch (err) {
      posthog.captureException(err)
      console.error(err)

      const handled = await handleNetworkError(err, navigate)

      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)

        return
      }

      toast.error("Error deleting sheet")
    } finally {
      closeAllSheets()
    }
  }

  function formatTitle(index: number) {
    return "Product " + `${padStart(`${index + 1}`, 2, "0")}`
  }

  function closeAllSheets() {
    packagingStore.setCurrSheet(PackagingSheets.None)
  }

  function handleClickDeleteProduct(product: ProductSchema, index: number) {
    setDeleteProduct(product)
    setDeleteProductIdx(index)

    packagingStore.setCurrSheet(PackagingSheets.ConfirmDeleteProduct)
  }

  if (!builder) {
    return false
  }

  return (
    <form>
      <FormContextProvider
        form={form.form}
        formSchema={schema}
        registry={{} as any}
        additional={form}
      >
        <div className="flex flex-col gap-4">
          {machine?.products.map((product, index) => (
            <ProductForm
              key={product.id}
              index={index}
              deptId={currDeptId}
              machineId={machine.id}
              deptName={department?.name || ""}
              machineName={machine.name || ""}
              schema={product}
              builder={builder}
              path={`products[${index}]`}
              title={formatTitle(index)}
              canEdit={canEdit}
              onClickDelete={() => handleClickDeleteProduct(product, index)}
            />
          ))}
        </div>
      </FormContextProvider>

      {canAddProduct && (
        <RoundedButton
          className="mt-6"
          icon={<Icon name="Plus" />}
          onClick={handleAddProduct}
        >
          New Product
        </RoundedButton>
      )}

      <ParamRemarksSheet form={form.form} showResample={false} />
      <RemarkDetailsSheet form={form.form} />

      <ConfirmActionSheet
        open={packagingStore.currSheet === PackagingSheets.ConfirmDeleteProduct}
        title="Delete Product"
        description="Are you sure you want to delete ? You won’t be able to undo this"
        onClose={closeAllSheets}
        onClickBack={closeAllSheets}
        onClickConfirm={() => handleDeleteProduct()}
      />
    </form>
  )
}

interface ProductFormProps {
  className?: string
  index: number
  title?: string
  deptId: string
  machineId: string
  schema: ProductSchema
  deptName: string
  machineName: string
  path: string
  builder: MachineFormSchemaBuilder
  canEdit?: boolean
  onClickDelete?: () => void
}

function ProductForm({
  className,
  schema,
  deptId,
  machineId,
  builder,
  path,
  title,
  deptName,
  machineName,
  canEdit,
  onClickDelete,
}: ProductFormProps) {
  const { form, setSection } = usePackagingFormContext()
  const packagingStore = useStore.use.packaging()

  useHandleChangeMarket()
  useHandleSelectProductOption()
  useHandleChangeSKU()
  useTotalLeakage()

  function useHandleChangeMarket() {
    useFieldChange({
      form,
      name: `${path}.sections[0].parameters[0]`,
      handler: (__event, context) => {
        const market = context.payload

        if (!market.value) {
          return
        }

        const certificationSection = builder.addSection(
          schema.id,
          market.value,
          PackagingParamSchemaType.CERTIFICATION,
          true
        )

        const generalSection = builder.addSection(
          schema.id,
          market.value,
          PackagingParamSchemaType.GENERAL_INFORMATION,
          true
        )
        packagingStore.setMachineFormSchema(
          packagingStore.currDeptId,
          builder.schema
        )

        // Reset the first section's values but keep the
        // value of the market parameter
        form.setValue(
          `${path}.sections[0]`,
          {
            id: generalSection?.id,
            type: PackagingParamSchemaType.GENERAL_INFORMATION,
          },
          {
            shouldTrigger: false,
          }
        )
        form.setValue(
          `${path}.sections[0].parameters[0]`,
          {
            id: generalSection?.parameters[0].id,
            value: market.value,
            recordedAt: new Date().toISOString(),
          },
          {
            shouldTrigger: false,
          }
        )

        form.setValue(`${path}.sections[1]`, {
          id: certificationSection?.id,
          type: PackagingParamSchemaType.CERTIFICATION,
        })
      },
    })
  }

  function useHandleChangeSKU() {
    useFieldChange({
      form,
      name: `${path}.sections[0].parameters[2]`,
      handler: async (__event, ctx) => {
        const sku = ctx?.payload?.value as string | undefined
        const market = form.getValues(`${path}.sections[0].parameters[0]`)
          ?.value as MarketType | undefined
        const productId = form.getValues(`${path}.sections[0].parameters[1]`)
          ?.value as string | undefined

        builder.setCurrSKU(sku || "")

        if (!sku || !market || !productId) {
          return
        }

        const certification = builder.addSection(
          schema.id,
          market,
          PackagingParamSchemaType.CERTIFICATION,
          true
        )

        if (certification) {
          form.remove(`${path}.sections[1]`, { keepValidators: false })
          setSection(`${path}.sections[1]`, certification)
        }

        packagingStore.setMachineFormSchema(
          packagingStore.currDeptId,
          builder.schema
        )
        await form.runAllValidators()
      },
    })
  }

  function useHandleSelectProductOption() {
    useFieldChange({
      form,
      name: `${path}.sections[0].parameters[1]`,
      handler: (__event, ctx) => {
        const product = ctx.payload

        form.setValue(`${path}.sections[0].parameters[2]`, undefined)
        builder.setSKUs(schema.id, product?.value || "")
        packagingStore.setMachineFormSchema(
          packagingStore.currDeptId,
          builder.schema
        )
      },
    })
  }

  function useTotalLeakage() {
    const debounce = useDebounceByHash(500)

    useEffect(() => {
      const product = form.getValues(path)
      if (!product) {
        packagingStore.setTotalLeakage(schema.id, 0)

        return
      }

      calculateLeakage(product)
    }, [])

    function calculateLeakage(product: ProductFormData) {
      let totalPackets = 0
      let leakedPackets = 0

      const leakageSections = product.sections.filter(
        (section) => section.type === PackagingParamSchemaType.LEAKAGE
      )

      for (const section of leakageSections) {
        const packets = Number(section.parameters[0].value)
        const leaked = Number(section.parameters[1].value)

        if (!isNaN(packets) && !isNaN(leaked)) {
          totalPackets += packets
          leakedPackets += leaked
        }
      }

      if (!leakedPackets || !totalPackets) {
        return packagingStore.setTotalLeakage(schema.id, 0)
      }

      if (totalPackets) {
        packagingStore.setTotalLeakage(
          schema.id,
          Number(((leakedPackets / totalPackets) * 100).toFixed(2))
        )
      } else {
        packagingStore.setTotalLeakage(schema.id, 0)
      }
    }

    useFieldChange({
      form,
      name: path,
      options: {
        deep: true,
      },
      handler(__e, ctx) {
        const product = ctx.payload as ProductFormData | undefined

        if (!product) {
          packagingStore.setTotalLeakage(schema.id, 0)

          return
        }

        debounce("calculateLeakage", () => {
          calculateLeakage(product)
        })
      },
    })
  }

  return (
    <CollapsibleCard
      className={cn("shadow-xs", className)}
      title={title}
      actions={<ProductActions onClickDelete={onClickDelete} />}
    >
      {schema.sections.map((section, sectionIndex) => (
        <SectionForm
          key={section.id}
          deptId={deptId}
          machineId={machineId}
          productId={schema.id}
          deptName={deptName}
          machineName={machineName}
          productName={title || ""}
          schema={section}
          builder={builder}
          path={`${path}.sections[${sectionIndex}]`}
          showLeakage={sectionIndex === schema.sections.length - 1}
          canEdit={canEdit}
        />
      ))}
    </CollapsibleCard>
  )
}

interface SectionFormProps {
  deptId: string
  productId: string
  machineId: string
  productName: string
  machineName: string
  deptName: string
  schema: ProductSectionSchema
  builder: MachineFormSchemaBuilder
  path: string
  showLeakage: boolean
  canEdit?: boolean
}

const SectionSheets = {
  CONFIRM_DELETE: "CONFIRM_DELETE",
  CONFIRM_SUBMIT: "CONFIRM_SUBMIT",
  NONE: "NONE",
} as const
type SectionSheets = EnumsOf<typeof SectionSheets>

function SectionForm({
  deptId,
  machineId,
  productId,
  deptName,
  machineName,
  productName,
  schema,
  builder,
  path,
  showLeakage,
  canEdit,
}: SectionFormProps) {
  const { form, setSection: addSection } = usePackagingFormContext()
  const { productIdx, sectionIdx } = useSchemaIndexes()
  const packagingStore = useStore.use.packaging()
  const [sheet, setSheet] = useState<SectionSheets>(SectionSheets.NONE)

  const sectionStatus = useWatch({
    form,
    name: `${path}.status`,
    defaultValue: FormParamStatus.PENDING,
  })
  const market = useWatch({
    form,
    name: `products[${productIdx}].sections[0].parameters[0].value`,
    defaultValue: MarketType.Domestic,
    immediate: true,
  })
  const submittedAt = useSubmittedAt()

  const validationStatus = useFieldStatus({
    form,
  })
  const canDelete = useCanDelete()

  function useCanDelete() {
    const [canDelete, setCanDelete] = useState(false)

    useEffect(() => {
      if (schema.type !== PackagingParamSchemaType.LEAKAGE || sectionIdx < 3) {
        setCanDelete(false)

        return
      }

      const machine = packagingStore.forms.find((form) => form.id === machineId)
      if (!machine) {
        setCanDelete(false)

        return
      }

      const section = machine.products.at(productIdx)?.sections.at(sectionIdx)
      if (!section) {
        return
      }

      if (section?.status === FormParamStatus.SUBMITTED) {
        setCanDelete(false)
      } else {
        setCanDelete(true)
      }
    }, [packagingStore.forms])

    return canDelete
  }

  function useSchemaIndexes() {
    const [productIdx, setProductIdx] = useState(-1)
    const [sectionIdx, setSectionIdx] = useState(-1)

    useEffect(() => {
      const parts = toPath(path)

      if (parts.length !== 4) {
        setProductIdx(-1)
        setSectionIdx(-1)

        return
      }

      setProductIdx(Number(parts[1]))
      setSectionIdx(Number(parts[3]))
    }, [path])

    return {
      productIdx,
      sectionIdx,
    }
  }

  function useSubmittedAt() {
    const [submittedAt, setSubmittedAt] = useState("")
    const value = useWatch({
      form,
      name: `${path}.submittedAt`,
    })

    useEffect(() => {
      if (!value) {
        setSubmittedAt("")

        return
      }

      try {
        setSubmittedAt(format(value, "HH:mm"))
      } catch {
        setSubmittedAt("")
      }
    }, [value])

    return submittedAt
  }

  async function submitTillCurr() {
    const parts = toPath(path)

    if (parts.length !== 4) {
      toast.error("Invalid form. Please contact support")
      posthog.captureException(new Error("Invalid form path"), { path: path })

      return
    }

    const productIdx = Number(parts[1])
    const sectionIdx = Number(parts[3])

    for (let i = 0; i <= sectionIdx; i++) {
      const sectionPath = `products[${productIdx}].sections[${i}]`
      const section = form.getValues(sectionPath) as
        | ProductSectionFormData
        | undefined

      if (
        !section ||
        section?.status === FormParamStatus.SUBMITTED ||
        !section?.parameters?.length
      ) {
        continue
      }

      form.setValue(
        `${sectionPath}`,
        {
          status: FormParamStatus.SUBMITTED,
          submittedAt: new Date().toISOString(),
        },
        { merge: true }
      )

      let sectionName = ""

      if (section.type === PackagingParamSchemaType.LEAKAGE) {
        sectionName = `Leakage ${sectionIdx - 1}`
      } else if (section.type === PackagingParamSchemaType.CERTIFICATION) {
        sectionName = "Certification"
      } else {
        sectionName = "General Information"
      }

      packagingStore.addSubmission({
        deptName: deptName,
        productName: productName,
        machineName: machineName,
        sectionName: sectionName,
        deptId,
        machineId,
        productId,
        sectionId: section.id || "",
      })

      for (let j = 0; j < section.parameters.length; j++) {
        const paramPath = `${sectionPath}.parameters[${j}]`
        form.setValue(`${paramPath}.status`, FormParamStatus.SUBMITTED, {
          shouldTrigger: false,
        })
      }
    }

    if (schema.type === PackagingParamSchemaType.CERTIFICATION) {
      await addLeakageSection()
    }

    closeAllSheets()
  }

  async function addLeakageSection() {
    const section = builder.addSection(
      productId,
      market,
      PackagingParamSchemaType.LEAKAGE,
      false
    )

    if (section) {
      const sectionPath = `products[${productIdx}].sections[${sectionIdx + 1}]`
      form.remove(sectionPath)
      addSection(sectionPath, section)
      await form.runAllValidators()
      packagingStore.setMachineFormSchema(
        packagingStore.currDeptId,
        builder.schema
      )
    }
  }

  function showAddLeakageButton() {
    if (!canEdit) {
      return false
    }

    const product = builder.schema.products.at(productIdx)

    if (!product) {
      return false
    }

    return !!(
      schema.type === PackagingParamSchemaType.LEAKAGE &&
      sectionStatus === FormParamStatus.SUBMITTED &&
      sectionIdx === product.sections.length - 1
    )
  }

  function getLeakage() {
    const productId = builder.schema.products.at(productIdx)?.id
    if (!productId) {
      return 0
    }

    return packagingStore.totalLeakage[productId] || 0
  }

  function handleDeleteSection() {
    form.remove(path)
    builder.deleteSection(productId, schema.id)
    packagingStore.setMachineFormSchema(deptId, builder.schema)
    closeAllSheets()
  }

  function handleClickDeleteSection() {
    setSheet(SectionSheets.CONFIRM_DELETE)
  }

  function closeAllSheets() {
    setSheet(SectionSheets.NONE)
  }

  return (
    <div className="w-full">
      {!!schema.name && (
        <h3
          className={cn(
            `
              flex justify-between items-center py-2.5 px-3 
              bg-band-fill uppercase text-sm font-semibold
            `,
            schema.type === PackagingParamSchemaType.GENERAL_INFORMATION &&
            "sr-only"
          )}
        >
          {schema.name}
          {submittedAt && <span>{submittedAt}</span>}

          {canDelete && (
            <button
              className="hover:cursor-pointer text-button"
              type="button"
              onClick={handleClickDeleteSection}
            >
              <Icon name="TrashCan" className="h-4 w-auto" />
            </button>
          )}
        </h3>
      )}

      <div className="grid grid-cols-2 gap-x-3 gap-y-4 px-3 pt-3 pb-4">
        {schema.parameters.map((param, index) => {
          if (
            sectionStatus === FormParamStatus.SUBMITTED &&
            schema.type === PackagingParamSchemaType.LEAKAGE &&
            index < 2
          ) {
            return false
          }

          return (
            <ParamForm
              key={param.id}
              schema={param}
              path={`${path}.parameters[${index}]`}
            />
          )
        })}
      </div>

      <div className="flex justify-end px-4 mt-4">
        {showAddLeakageButton() && (
          <RoundedButton
            icon={<Icon name="Plus" />}
            onClick={addLeakageSection}
          >
            New Entry
          </RoundedButton>
        )}
      </div>

      {schema.type === PackagingParamSchemaType.LEAKAGE && showLeakage && (
        <div className="px-3 mt-6">
          <div
            className={`
              flex justify-between items-center border border-dashed border-blue
              bg-blue-fill text-sm h-8 px-3 font-medium
            `}
          >
            <div>Total Leakage</div>
            <div className="text-base">{getLeakage()} %</div>
          </div>
        </div>
      )}

      {schema.type !== PackagingParamSchemaType.GENERAL_INFORMATION &&
        sectionStatus !== FormParamStatus.SUBMITTED && (
          <div className="px-3 mt-4">
            <Button
              className="w-full"
              disabled={validationStatus !== FormParamStatus.VALID}
              onClick={() => setSheet(SectionSheets.CONFIRM_SUBMIT)}
            >
              Confirm
            </Button>
          </div>
        )}

      <ConfirmActionSheet
        open={sheet === SectionSheets.CONFIRM_DELETE}
        title="Delete Leakage"
        description="Are you sure you want to delete ? You won’t be able to undo this"
        onClose={closeAllSheets}
        onClickBack={closeAllSheets}
        onClickConfirm={handleDeleteSection}
      />

      <ConfirmActionSheet
        open={sheet === SectionSheets.CONFIRM_SUBMIT}
        title="Submit Section"
        description="Are you sure you want to submit ? You won’t be able to edit any readings later"
        onClose={closeAllSheets}
        onClickBack={closeAllSheets}
        onClickConfirm={submitTillCurr}
      />
    </div>
  )
}

interface ParamFormProps {
  schema: PackagingParamWidgetSchema
  path: string
}

function ParamForm({ schema, path }: ParamFormProps) {
  function getClassname() {
    if (schema.mode === ParamSchemaMode.INFO) {
      return "col-span-1"
    }

    return "col-span-2"
  }

  if (schema.type === ParamSchemaType.RADIO) {
    return (
      <RadioGroupWidget
        className="col-span-2"
        schema={schema as RadioParamSchema}
        path={path}
      />
    )
  }

  if (schema.type === ParamSchemaType.DROPDOWN) {
    if (schema.name === "Leakage Type") {
      return (
        <LeakageWidget
          className="col-span-2"
          path={path}
          schema={schema as DropdownParamSchema}
        />
      )
    }

    return (
      <DropdownWidget
        className={getClassname()}
        schema={schema as DropdownParamSchema}
        path={path}
      />
    )
  }

  if (
    schema.type === ParamSchemaType.VALUE_TEXT ||
    schema.type === ParamSchemaType.VALUE_NUMBER
  ) {
    return (
      <LimitWidget
        className={getClassname()}
        schema={schema as ParamSchema}
        path={path}
      />
    )
  }

  if (schema.type === ParamSchemaType.CHOICE) {
    return (
      <ChoiceWidget
        className={getClassname()}
        schema={schema as ParamSchema}
        path={path}
      />
    )
  }

  if (schema.type === ParamSchemaType.DATE) {
    return (
      <DatePickerWidget
        className={getClassname()}
        schema={schema as ParamSchema}
        path={path}
      />
    )
  }

  return <div>{schema.type}</div>
}

interface ProductActionsProps {
  showDelete?: boolean
  onClickDelete?: () => void
}

function ProductActions(_props: ProductActionsProps) {
  return (
    <div className="flex justify-end">
      {/* Delete button commented out as per requirements */}
    </div>
  )
}
